import React, { useEffect, useRef, useState } from "react";
import { CSSProperties } from "styled-components";
import EmbudoBack720 from "../../../assets/Embudo-Back-720.webp";
import Plataforma from "../../../assets/plataforma-front.webp";
import Ballot from "../Ballot/Ballot";
import InfoBoard from "../InfoBoard/InfoBoard";
import "./styles.css";

interface Result {
  number: number;
  colorCode: string;
}

interface StageProps {
  initGame: boolean;
  results: Result[];
  style: CSSProperties;
  onEnded: () => void;
}

const BackgroundVideoURL =
  "https://lotteryblob.blob.core.windows.net/lottery-files/bg-video.webm";
const CoinsFallingURL =
  "https://lotteryblob.blob.core.windows.net/lottery-files/coins-falling.webm";

async function openIndexedDB() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open("videoDB", 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("videos")) {
        db.createObjectStore("videos");
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeVideoInDB(key: string, blob: Blob) {
  const db = await openIndexedDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction("videos", "readwrite");
    const store = tx.objectStore("videos");
    store.put(blob, key);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getVideoFromDB(key: string) {
  const db = await openIndexedDB();
  return new Promise<Blob | undefined>((resolve, reject) => {
    const tx = db.transaction("videos", "readonly");
    const store = tx.objectStore("videos");
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

const GifStage: React.FC<StageProps> = ({ initGame, results, style, onEnded }) => {
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const ballotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const embudoRailContainerRef = useRef<HTMLDivElement>(null);
  const [showResults, setShowResults] = useState(false);
  const [backgroundVideo, setBackgroundVideo] = useState<string | null>(null);
  const [coinsFallingVideo, setCoinsFallingVideo] = useState<string | null>(null);

  useEffect(() => {
    async function loadVideos() {
      try {
        let bgVideoBlob = await getVideoFromDB("bg-video");
        let coinsVideoBlob = await getVideoFromDB("coins-falling");

        if (bgVideoBlob) {
          setBackgroundVideo(URL.createObjectURL(bgVideoBlob));
        } else {
          fetch(BackgroundVideoURL)
            .then((res) => res.blob())
            .then((blob) => {
              setBackgroundVideo(URL.createObjectURL(blob));
              storeVideoInDB("bg-video", blob);
            });
        }

        if (coinsVideoBlob) {
          setCoinsFallingVideo(URL.createObjectURL(coinsVideoBlob));
        } else {
          fetch(CoinsFallingURL)
            .then((res) => res.blob())
            .then((blob) => {
              setCoinsFallingVideo(URL.createObjectURL(blob));
              storeVideoInDB("coins-falling", blob);
            });
        }
      } catch (error) {
        console.error("Error al cargar videos desde IndexedDB:", error);
      }
    }

    loadVideos();
  }, []);

  const animateBall = (
    ballot: HTMLDivElement | null,
    endPosition: number,
    duration: number = 3,
    delay: number = 0.3
  ) => {
    if (!ballot) return;

    const keyframes = [
      {
        opacity: `0`,
        transform: `translateX(100) translateY(0) `,
        offset: 0,
      },
      {
        opacity: `0`,
        transform: `translateX(91cqw) translateY(28cqw) `,
        offset: 0.67,
      },
      {
        opacity: `1`,
        transform: `translateX(80cqw) translateY(28cqw) `,
        offset: 0.7,
      },
      {
        transform: `translateX(${endPosition}cqw) translateY(29cqw) `,
        offset: 0.9,
      },
      {
        transform: `translateX(${endPosition - 1.5}cqw) translateY(29cqw) `,
        offset: 0.95,
      },
      {
        transform: `translateX(${endPosition}cqw) translateY(29cqw) `,
        offset: 1,
      },
    ];

    ballot.animate(keyframes, {
      duration: duration * 1000,
      iterations: 1,
      delay: delay * 1000,
      easing: "ease-in-out",
      fill: "forwards",
    });
  };

  const startGame = () => {
    const bgVideo = bgVideoRef.current;
    if (bgVideo) {
      setTimeout(() => {
        bgVideo.play();
      }, 600);

      bgVideo.onended = () => {
        setShowResults(true);
        setTimeout(() => {
          window.parent.postMessage("gameFinished", "*");
          onEnded();
        }, 10000);
      };

      trackBalls();
    }
  };

  const trackBalls = () => {
    const ballConfigurations = [
      { endPosition: 60, duration: 1, delay: 2.5 },
      { endPosition: 64 - 10, duration: 1, delay: 2.2 },
      { endPosition: 64 - 9 * 1.87, duration: 1, delay: 1.9 },
      { endPosition: 64 - 8.2 * 2.8, duration: 1, delay: 1.6 },
      { endPosition: 64.4 - 7.67 * 3.8, duration: 1, delay: 1.3 },
      { endPosition: 64.34 - 7.45 * 4.71, duration: 1, delay: 1 },
    ];

    ballConfigurations.forEach((config, index) => {
      if (ballotRefs.current[index]) {
        animateBall(
          ballotRefs.current[index],
          config.endPosition,
          config.duration,
          config.delay
        );
      }
    });
  };

  useEffect(() => {
    if (initGame) {
      startGame();
    }
  }, [initGame]);

  return (
    <div
    className="stage"
    style={{
      position: "relative",
      width: "100%",
      height: "auto",
      aspectRatio: "16/9",
      ...style,
    }}
  >
    {showResults && (
      <div
        className="fade-in"
        style={{
          position: "absolute",
          zIndex: 106,
          width: "100%",
          height: "auto",
          aspectRatio: "16/9",
          overflow: "hidden",
        }}
      >
        <div className="row h-75 justify-content-center align-items-center">
          <div className="col-12 col-md-6 slide-in-elliptic-top-fwd-enter-active">
            <h1 className="display-1 fw-bold result-text text-center">
              RESULTADO
            </h1>
            <div className="card result-card">
              <div className="card-body d-flex flex-row-reverse justify-content-center">
                {results.map((result, index) => (
                  <Ballot
                    key={index}
                    className="ballot slide-in-elliptic-top-fwd-enter-active"
                    style={{ animationDelay: `${index * 0.2}s` }}
                    color={result.colorCode}
                  >
                    {result.number}
                  </Ballot>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="row h-75 justify-content-center align-items-center">
          <div className="col-12 col-md-6 slide-in-elliptic-top-fwd-enter-active">
            <h1 className="display-1 fw-bold result-text text-center">
              RESULTADO
            </h1>
            <div className="table-responsive">
              <table className="table table-borderless text-center">
                <tbody>
                  <tr>
                    {results.map((result, index) => (
                      <td
                        key={index}
                        className="align-middle"
                        style={{
                          animationDelay: `${index * 0.2}s`,
                          animation:
                            "slide-in-elliptic-top-fwd-enter-active 0.6s both",
                          width: "100px",
                          height: "100px",
                          borderRadius: "50%",
                          backgroundColor: "transparent",
                          border: "2px dashed white",
                        }}
                      >
                        <Ballot color={result.colorCode}>
                          {result.number}
                        </Ballot>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    )}

    {(showResults && coinsFallingVideo ) &&  (
      <video
        muted
        autoPlay
        loop
        className="layer fade-in"
        style={{ zIndex: 105 }}
      >
        <source src={coinsFallingVideo} type="video/webm"></source>
      </video>
    )}

    {initGame && (
      <div
        style={{
          position: "absolute",
          zIndex: 107,
          width: "100%",
          height: "auto",
          aspectRatio: "16/9",
          overflow: "hidden",
        }}
      >
        <div className="justify-content-end position-absolute w-100 h-100 p-4">
          <InfoBoard result={results} />
        </div>
      </div>
    )}

   {
    backgroundVideo &&  <video
    ref={bgVideoRef}
    preload="auto"
    muted
    className="layer sync-layer"
    style={{ zIndex: 0 }}
  >
    <source src={backgroundVideo} type="video/webm"></source>
  </video>
   }

    <div className="layer" ref={embudoRailContainerRef}>
      <div className="position-relative">
        <img
          className="embudo-back"
          alt=""
          src={EmbudoBack720}
          style={{ transform: "scaleX(-1)" }}
        />

        <div className="ballot-container">
          {results.slice(0, 6).map((result, index) => (
            <div
              key={index}
              className="ballot"
              ref={(el) => (ballotRefs.current[index] = el)}
            >
              <div className="position-relative h-100">
                <div className="ballot-result-container d-flex flex-column">
                  <h1 className="fw-bolder my-auto">{result.number}</h1>
                </div>
                <div
                  style={{ background: result.colorCode }}
                  className="ballot-color-filter"
                ></div>
              </div>
            </div>
          ))}
        </div>

        <img
          className="embudo-front"
          style={{ transform: "scaleX(-1)" }}
          alt=""
          src={Plataforma}
        />
      </div>
    </div>
  </div>
  );
};

export default GifStage;
